import React, { useCallback, useMemo } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import classnames from 'classnames';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button';
import { isNil, partial } from 'lodash';
import PropTypes from 'prop-types';

import MissingBreaksIssue from 'features/timesheets/TimesheetsPage/TimecardModal/ReadMode/TimecardSection/Issues/MissingBreaks';
import { duration, toMoment } from 'features/timesheets/util';

import { cxHelpers } from 'util/className';
import { momentInZone } from 'util/dateTime';
import { toI18n } from 'util/i18n';
import { EVENT_ACTIONS } from 'util/tracking_constants';

import Section from '../../Section';

import IndividualBreak from './IndividualBreak';
const { cx, cxEl } = cxHelpers('TimebreakSection');

function numberOfMinutes(timebreak, inProgress = false) {
  const defaultEndAt = inProgress ? momentInZone() : null;

  const endAt = isNil(timebreak.get('end_at'))
    ? defaultEndAt
    : toMoment(timebreak.get('end_at'));
  const startAt = toMoment(timebreak.get('start_at'));
  return isNil(endAt) ? 0 : endAt.diff(startAt, 'minutes');
}

const breakDuration = (timebreak, inProgress) =>
  duration(numberOfMinutes(timebreak, inProgress));

const expandedContent = (timebreaks, inProgress, onEndBreak) =>
  timebreaks.isEmpty()
    ? null
    : timebreaks.map((timebreak, i) => (
        <IndividualBreak
          timebreak={timebreak}
          key={i}
          breakDuration={breakDuration(timebreak, inProgress)}
          inProgress={inProgress}
          onEndBreak={partial(onEndBreak, timebreak)}
        />
      ));

function breaksDuration(timebreaks, inProgress) {
  return duration(
    timebreaks.reduce(
      (total, n) =>
        total + (n.get('employee_waived') ? 0 : numberOfMinutes(n, inProgress)),
      0
    )
  );
}

const TimebreakSection = ({
  addTimebreak,
  breakPenaltiesEnabled,
  dismissMissedBreaks,
  expandBreaks,
  shift,
  submitChanges,
  setChangesMade,
}) => {
  function handleEndBreak(timebreak) {
    const index = shift
      .get('timebreaks')
      .findIndex(b => b.get('id') === timebreak.get('id'));
    const timebreaks = shift
      .get('timebreaks')
      .setIn([index, 'end_at'], momentInZone().format('MM/DD/YYYY HH:mm'))
      .toJS();

    submitChanges(shift.get('job_id'), {
      shift_id: shift.get('id'),
      timebreaks,
    });
  }

  const inProgress =
    shift.get('status') === 'in_progress' || shift.get('status') === 'on_break';

  const fetchBreaks = useCallback(
    ({ isPaid }) => {
      const requiredBreaks = shift
        .get('timebreaks')
        .filter(timeBreak => timeBreak.get('paid') === isPaid)
        .toList();
      return requiredBreaks;
    },
    [shift]
  );

  const paidBreaks = useMemo(
    () =>
      fetchBreaks({
        isPaid: true,
      }),
    [fetchBreaks]
  );

  const unpaidBreaks = useMemo(
    () =>
      fetchBreaks({
        isPaid: false,
      }),
    [fetchBreaks]
  );

  function getBreakPenalty(breakPenalty) {
    return (
      <Box>
        <Box vcenter>
          <Text
            i18n="timecard_modal_v3.timebreaks_section.generic.hours"
            i18nProps={{ count: breakPenalty || 0 }}
          />
        </Box>
      </Box>
    );
  }

  function combineBreakDurations(timebreaks, missingBreaksIssue) {
    const openBreak = timebreaks.find(b => !b.get('end_at'));
    const hasOpenBreaks = inProgress && !!openBreak;

    return (
      <Box>
        <Box vcenter>
          <Text color={hasOpenBreaks ? 'green500' : 'mono900'}>
            {breaksDuration(timebreaks, inProgress)}
          </Text>
          {hasOpenBreaks && (
            <Button
              variant="tertiary"
              onClick={partial(handleEndBreak, openBreak)}
            >
              <Text i18n="timecard_modal_v3.timebreaks_section.end_break" />
            </Button>
          )}
        </Box>

        {missingBreaksIssue && (
          <Box>
            <MissingBreaksIssue
              issue={missingBreaksIssue}
              addTimebreak={addTimebreak}
              dismissMissedBreaks={dismissMissedBreaks}
              setChangesMade={setChangesMade}
            />
          </Box>
        )}
      </Box>
    );
  }

  const paidMissingBreaksIssue = useMemo(
    () =>
      shift
        .get('issues')
        .filter(issue => issue.get('type') === 'missing_paid_breaks')
        .first(),
    [shift]
  );

  const unpaidMissingBreaksIssue = useMemo(
    () =>
      shift
        .get('issues')
        .filter(issue => issue.get('type') === 'missing_unpaid_breaks')
        .first(),
    [shift]
  );

  return (
    <Box className={cx()}>
      <Section
        trackingAction={EVENT_ACTIONS.BREAK_DETAILS_EXPANDED_V3}
        className={cxEl('paid-breaks')}
        title={toI18n('timecard_modal_v3.timebreaks_section.read_paid')}
        iconName="Break"
        collapsedContent={combineBreakDurations(
          paidBreaks,
          paidMissingBreaksIssue
        )}
        expandedContent={expandedContent(
          paidBreaks,
          inProgress,
          handleEndBreak
        )}
        autoExpand={expandBreaks}
      />
      <Section
        bottomBorder={!breakPenaltiesEnabled}
        bottomPadding={!breakPenaltiesEnabled}
        trackingAction="Break details expanded_v3"
        className={classnames(cxEl('unpaid-breaks'), {
          withoutBreakPenalties: !breakPenaltiesEnabled,
        })}
        title={toI18n('timecard_modal_v3.timebreaks_section.read_unpaid')}
        collapsedContent={combineBreakDurations(
          unpaidBreaks,
          unpaidMissingBreaksIssue
        )}
        expandedContent={expandedContent(
          unpaidBreaks,
          inProgress,
          handleEndBreak
        )}
        autoExpand={expandBreaks}
      />
      {breakPenaltiesEnabled && (
        <Section
          className={cxEl('break-penalty')}
          title={toI18n('timecard_modal_v3.timebreaks_section.break_penalty')}
          collapsedContent={getBreakPenalty(shift.get('break_penalty'))}
          bottomBorder
          bottomPadding
        />
      )}
    </Box>
  );
};

TimebreakSection.propTypes = {
  addTimebreak: PropTypes.func.isRequired,
  breakPenaltiesEnabled: PropTypes.bool,
  dismissMissedBreaks: PropTypes.func.isRequired,
  shift: ImmutablePropTypes.map.isRequired,
  submitChanges: PropTypes.func.isRequired,
  expandBreaks: PropTypes.bool,
  setChangesMade: PropTypes.func.isRequired,
};

export default TimebreakSection;
